"use client";

import type { GenerateSttParams } from "@/app/_api/generateStt";
import type { Media, OmniboxDemo } from "@mw/shared/payload-types";
import type { ReactNode } from "react";

import { generateStt } from "@/app/_api/generateStt";
import { captureEvent } from "@/app/_analytics/analytics";
import { toast } from "@/app/_providers/toast";
import { Button } from "@components/core/Button";
import { MEDIA_CONTENT_TYPES } from "@components/demos/STT/utils";
// TODO: migrate to react aria alternative
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Image } from "@ui/components/core/Image";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/components/core/Select";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import { cn } from "@ui/lib/utils";
import { isEntity } from "@utilities/payload";
import { cva } from "class-variance-authority";
import { AudioLinesIcon, RotateCcw, TextIcon } from "lucide-react";
import * as m from "motion/react-m";
import { memo, useCallback } from "react";
import React from "react";
import { type DropzoneRootProps, useDropzone } from "react-dropzone";

import type { BlockDemoProps } from "../BlockDemos";
import type { BlockDemoPropsV2 } from "../BlockDemosV2";
import type { TranscriptionResponseWithSegments } from "./STTStore";
import type { TranscriptionResponse } from "./types";

import { LimitReachedOverlay } from "../LimitReachedOverlay";
import { OmniboxComponentWrapper } from "../OmniboxComponentWrapper";
import { type SampleOption, SamplePicker } from "../SamplePicker";
import { EventName } from "@mw/shared/analyticsEventNames";
import {
  STTCache,
  STTStoreProvider,
  type TabData,
  type ToolItem,
  type ToolTabData,
  type TranscriptionItem,
  type TranscriptionTabData,
  getSttDemoExampleClickedPayload,
  useSTTActions,
  useSTTContext,
  useSTTSelectedTabData,
  useSTTSelectedTabId,
  useSTTTabData,
  useSTTTools,
  useSTTTranscriptions,
  useSTTVariant,
} from "./STTStore";
import { TranslationContext, useSTTTranslationCtx, useSTTTranslations } from "./STTTranslations";
import { STTfromURL } from "./STTfromURL";
import { STTRealtime as STTRealtimeUI } from "./STTRealtime";
import { urlToFilename } from "./utils";
import { OpenFileButton } from "./view/OpenFileButton";
import STTAudioPlayer from "./view/STTAudioPlayer";
import TranscriptView from "./view/TranscriptView";
import { TranscribingView } from "./view/TranscritLoadingView";
import clsx from "clsx";
import { appUrls } from "@/constants";
import { OmniboxSimpleWrapper } from "../OmniboxSimpleWrapper";

// TODO: Change to match back-end file input size limit (currently 100mb)
const MAX_UPLOAD_SIZE_MB = 300;
const BYTES_PER_MB = 1024 * 1024;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * BYTES_PER_MB;

export type SampleFile = Omit<NonNullable<OmniboxDemo["sttSampleFiles"]>[number], "response"> & {
  response?: TranscriptionResponseWithSegments;
};
export interface STTProps extends Pick<BlockDemoPropsV2, "initiallyVisible" | "size"> {
  customSttGenerator?: (props: GenerateSttParams) => Promise<TranscriptionResponse>;
  sttIcons?: {
    fromIcon?: Media | null | string;
    toIcon?: Media | null | string;
  } | null;
  sttSampleFiles?: SampleFile[] | null;
  sttTabs?: OmniboxDemo["sttTabs"];
  sttTranslations?: OmniboxDemo["sttTranslations"];
  variant?: "default" | "simple";
  enabledFeatures?: string[];
  withUploadFile?: boolean;
}

// This is used to keep feature parity with previous implementation
export interface GlobalSTT {
  abortController?: AbortController | null;
  file?: File | null;
  fromIcon: ReactNode;
  id: string;
  isTranscribing: boolean;
  toIcon: ReactNode;
  transcription?: TranscriptionResponseWithSegments;
  uri?: string;
}

export const STT = memo(STTComponent);

export function STTComponent({
  sttSampleFiles,
  sttIcons,
  sttTranslations = {},
  customSttGenerator,
  sttTabs,
  withUploadFile = true,
  variant = "default",
  size,
  initiallyVisible,
  enabledFeatures = [],
}: STTProps) {
  const t = useSTTTranslations(sttTranslations);

  // Create tools list from sttTabs
  const tools: ToolItem[] =
    sttTabs?.map(tab => ({
      id: tab.id!,
      label: tab.name,
    })) || [];

  // Create transcriptions list from sttSampleFiles
  const transcriptions: TranscriptionItem[] =
    sttSampleFiles?.map(sample => ({
      id: sample.id!,
      label: sample.name,
    })) || [];

  // Create tabData map for both tools and transcriptions
  const tabData: Record<string, TabData> = {};

  // Add tool tab data
  sttTabs?.forEach(tab => {
    if (tab.id) {
      tabData[tab.id] = {
        componentId: "url-input-tool",
        isTranscribing: false,
        icon: tab.icon,
        urlInputTexts: tab.urlInputTexts, // Additional data for URL input tool
        id: tab.id,
      } as ToolTabData;
    }
  });

  // Add transcription tab data
  sttSampleFiles?.forEach(sample => {
    const uri = isEntity(sample.file) ? sample.file.url! : sample.file;
    tabData[sample.id!] = {
      id: sample.id!,
      componentId: "transcription-viewer",
      isTranscribing: false,
      icon: sample.icon!,
      uri,
      transcription: sample.response,
    } as TranscriptionTabData;
  });

  const cache = new STTCache({
    cache:
      sttSampleFiles?.reduce(
        (acc, sample) => {
          const key = urlToFilename(typeof sample.file === "string" ? sample.file : sample.file.url!);
          acc[key] = sample.response;
          return acc;
        },
        {} as Record<string, TranscriptionResponseWithSegments | undefined>,
      ) || {},
  });

  // Determine initial selected tab
  // When realtime is enabled and there are no tools, stay on home screen (null). Otherwise, auto-select first tool/transcription.
  const withRealtime = enabledFeatures.includes("realtime");
  const initialSelectedTabId =
    withRealtime && tools.length === 0 ? null : (tools.length > 0 ? tools[0]?.id : transcriptions[0]?.id) || null;

  const fromIcon = sttIcons?.fromIcon ? (
    <Image className='h-32 w-32' resource={sttIcons.fromIcon} />
  ) : (
    <AudioLinesIcon className='h-32 w-32' />
  );
  const toIcon = sttIcons?.toIcon ? (
    <Image className='h-32 w-32' resource={sttIcons.toIcon} />
  ) : (
    <TextIcon className='h-32 w-32' />
  );

  return (
    <STTStoreProvider
      cache={cache}
      initialGlobalSTT={{
        isTranscribing: false,
        id: "global",
        fromIcon,
        toIcon,
      }}
      initialSelectedtab={initialSelectedTabId}
      isRealtimeEnabled={withRealtime}
      sttGenerator={customSttGenerator ?? generateStt}
      tabData={tabData}
      tools={tools}
      transcriptions={transcriptions}
      variant={variant}
    >
      <TranslationContext.Provider value={{ t }}>
        <STTContent initiallyVisible={initiallyVisible} size={size} withUploadFile={withUploadFile} />
      </TranslationContext.Provider>
    </STTStoreProvider>
  );
}

function Wrapper({
  variant,
  children,
  size,
  initiallyVisible,
}: {
  variant?: "default" | "simple";
  children: React.ReactNode;
  size: string;
  initiallyVisible: boolean;
}) {
  if (variant === "simple") {
    return <OmniboxSimpleWrapper className='h-[21.25rem] md:h-[23.5rem]'>{children}</OmniboxSimpleWrapper>;
  }
  return (
    <OmniboxComponentWrapper
      component='stt'
      initiallyVisible={initiallyVisible}
      size={size || "lg"}
      withBorder={true}
      shadow='combined'
    >
      {children}
    </OmniboxComponentWrapper>
  );
}

function STTContent({
  withUploadFile,
  initiallyVisible,
  size,
}: {
  initiallyVisible: BlockDemoProps["initiallyVisible"];
  size: BlockDemoProps["size"];
  withUploadFile?: boolean;
}) {
  const { showLimitReachedOverlay, variant } = useSTTContext();
  const t = useSTTTranslationCtx();

  return (
    <>
      <Wrapper initiallyVisible={initiallyVisible} size={size || "lg"} variant={variant}>
        <div className='relative flex h-full w-full flex-col overflow-hidden'>
          {showLimitReachedOverlay && (
            <LimitReachedOverlay
              rounded
              description={t("widget.quota_exceeded_description")}
              title={t("widget.quota_exceeded_title")}
              variant={variant === "simple" ? "redesigned" : "default"}
              ctaLink={appUrls.speechToText}
            />
          )}
          <OverviewView withUploadFile={withUploadFile} />
        </div>
      </Wrapper>
      {variant === "simple" && <SamplePickerRow className='mt-8' />}
    </>
  );
}

function OverviewView({ withUploadFile }: { withUploadFile?: boolean }) {
  const {
    selectedTabData,
    tools,
    transcriptions,
    globalSTT,
    actions: { globalTranscribeFile },
    variant,
  } = useSTTContext();
  const isMobile = useIsMobile();

  // Visible dropzone for the main drop area (only when no tab/sample selected)
  const {
    isDragActive,
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    multiple: false,
    accept: Object.fromEntries(MEDIA_CONTENT_TYPES.map(type => [type, []])),
    maxSize: MAX_UPLOAD_SIZE_BYTES,
    onDropAccepted: files => {
      handleFileSelected(files[0]);
    },
    onDropRejected: rejected => {
      const { file, errors } = rejected[0];
      let errorMessage = `"${file.name}" could not be processed. Please try another file.`;

      if (errors.some(e => e.code === "file-invalid-type")) {
        errorMessage = `"${file.name}" is not supported. Please choose an audio or video file.`;
      } else if (errors.some(e => e.code === "file-too-large")) {
        errorMessage = `"${file.name}" exceeds the ${MAX_UPLOAD_SIZE_MB}MB size limit.`;
      }

      toast.error(errorMessage);
    },
  });

  const handleFileSelected = useCallback(
    (file: File) => {
      globalTranscribeFile(file);
    },
    [globalTranscribeFile],
  );

  return (
    <>
      {/* Invisible dropzone that's always active when withUploadFile is true */}
      {withUploadFile && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <input {...getInputProps()} />
        </div>
      )}

      <div className={cn("flex h-full w-full", variant !== "simple" && "bg-dark")}>
        {(tools.length > 0 || transcriptions.length > 0) && variant === "default" && (
          <SampleSidebar openFileDialog={openFileDialog} withUploadFile={withUploadFile} />
        )}

        <div className={cn("relative flex flex-1 flex-col gap-8 overflow-hidden p-4")}>
          {/* Show tab content if a tab is selected */}
          {selectedTabData ? (
            <TabViewRenderer openFileDialog={openFileDialog} tabData={selectedTabData} />
          ) : (
            <>
              <GlobalSTTRenderer
                getRootProps={getRootProps}
                isDragActive={isDragActive}
                openFileDialog={openFileDialog}
              />
            </>
          )}

          {isMobile && (tools.length > 0 || transcriptions.length > 0) && variant === "default" && (
            <div className='flex justify-between p-4 lg:p-8'>
              <SampleSelect disabled={globalSTT.isTranscribing} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const SampleSelect = ({ disabled }: { disabled?: boolean }) => {
  // Use specific hooks instead of useSTTContext to minimize re-renders
  const tools = useSTTTools();
  const transcriptions = useSTTTranscriptions();
  const tabData = useSTTTabData();
  const selectedTabId = useSTTSelectedTabId();
  const selectedTabData = useSTTSelectedTabData();
  const { setSelectedTabIdWithAutoTranscribe } = useSTTActions();

  const t = useSTTTranslationCtx();

  const handleSelectionChange = (value: string) => {
    if (value !== selectedTabId) {
      const clicked = getSttDemoExampleClickedPayload(value, transcriptions);
      if (clicked) captureEvent(EventName.mw_stt_example_clicked, clicked);
    }
    setSelectedTabIdWithAutoTranscribe(value);
  };

  return (
    <div className='flex flex-col gap-4'>
      <Select disabled={disabled} onValueChange={handleSelectionChange} value={selectedTabId || ""}>
        <SelectTrigger
          className='max-w-[200px]'
          icon={
            selectedTabData?.icon &&
            (React.isValidElement(selectedTabData.icon) ? (
              selectedTabData.icon
            ) : (
              <Image className='h-20 w-20' resource={selectedTabData.icon} />
            ))
          }
        >
          <SelectValue placeholder={t("widget.placeholder")}>
            <p className='w-[100px] truncate'>
              {selectedTabId
                ? tools.find(t => t.id === selectedTabId)?.label ||
                  transcriptions.find(t => t.id === selectedTabId)?.label
                : ""}
            </p>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className='max-h-[350px]'>
          {/* Tools Section */}
          {tools && tools.length > 0 && (
            <>
              <div className='f-ui-03 px-8 py-4 opacity-80'>{t("sidebar.transcribe")}</div>
              {tools.map(tool => {
                const toolTabData = tabData[tool.id];
                return (
                  <SelectItem
                    className='max-w-[200px] overflow-hidden truncate'
                    icon={
                      toolTabData?.icon &&
                      (React.isValidElement(toolTabData.icon) ? (
                        toolTabData.icon
                      ) : (
                        <Image className='h-20 w-20' resource={toolTabData.icon} />
                      ))
                    }
                    key={tool.id}
                    value={tool.id}
                  >
                    <span className='f-ui-06 truncate'>{tool.label}</span>
                  </SelectItem>
                );
              })}
              {transcriptions && transcriptions.length > 0 && <div className='border-gray-200 mx-8 my-4 border-t' />}
            </>
          )}

          {/* Transcriptions Section */}
          {transcriptions && transcriptions.length > 0 && (
            <>
              <div className='f-ui-03 px-8 py-4 opacity-80'>{t("sidebar.transcripts")}</div>
              {transcriptions.map(transcription => {
                const transcriptionTabData = tabData[transcription.id];
                return (
                  <SelectItem
                    className='max-w-[200px] overflow-hidden truncate'
                    icon={
                      transcriptionTabData?.icon &&
                      (React.isValidElement(transcriptionTabData.icon) ? (
                        transcriptionTabData.icon
                      ) : (
                        <Image className='h-20 w-20' resource={transcriptionTabData.icon} />
                      ))
                    }
                    key={transcription.id}
                    value={transcription.id}
                  >
                    <span className='f-ui-06'>{transcription.label}</span>
                  </SelectItem>
                );
              })}
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

const SamplePickerRow = ({ className }: { className?: string }) => {
  const {
    actions: { setSelectedTabIdWithAutoTranscribe },
    transcriptions,
    selectedTabId,
  } = useSTTContext();

  const sampleOptions: SampleOption[] = transcriptions.map(transcription => ({
    label: transcription.label,
    value: transcription.id,
  }));

  const handleSampleSelected = (sample: SampleOption) => {
    if (sample.value !== selectedTabId) {
      const clicked = getSttDemoExampleClickedPayload(sample.value, transcriptions);
      if (clicked) captureEvent(EventName.mw_stt_example_clicked, clicked);
    }
    setSelectedTabIdWithAutoTranscribe(sample.value);
  };

  if (transcriptions.length === 0) {
    return null;
  }

  return (
    <SamplePicker
      className={className}
      onSelected={handleSampleSelected}
      samples={sampleOptions}
      selectedValue={selectedTabId}
    />
  );
};

const SampleSidebar = ({
  disabled,
  openFileDialog,
  withUploadFile = false,
}: {
  disabled?: boolean;
  openFileDialog: () => void;
  withUploadFile?: boolean;
}) => {
  const {
    actions: { setSelectedTabIdWithAutoTranscribe },
    tools,
    transcriptions,
    tabData,
    selectedTabId,
  } = useSTTContext();
  const t = useSTTTranslationCtx();

  const handleSelectionChange = (value: string) => {
    if (value !== selectedTabId) {
      const clicked = getSttDemoExampleClickedPayload(value, transcriptions);
      if (clicked) captureEvent(EventName.mw_stt_example_clicked, clicked);
    }
    setSelectedTabIdWithAutoTranscribe(value);
  };

  return (
    <div className='hidden w-2/6 max-w-xs shrink-0 flex-col gap-5 bg-dark p-4 pr-2 lg:flex'>
      <div className='scrollbar-none flex-1 overflow-y-auto'>
        {/* Single RadioGroup for both tools and transcriptions */}
        <RadioGroup.Root className={cn("space-y-2")} onValueChange={handleSelectionChange} value={selectedTabId || ""}>
          {/* Tools Section */}
          {tools && tools.length > 0 && (
            <div className='mb-6'>
              <div className='p-12 opacity-80'>
                <h3 className='f-ui-03 mb-4'>{t("sidebar.transcribe")}</h3>
              </div>
              {tools.map(tool => {
                const toolTabData = tabData[tool.id];
                return (
                  <RadioGroup.Item
                    className='relative w-full rounded-lg p-12 focus:outline-darkest'
                    disabled={disabled}
                    key={tool.id}
                    value={tool.id}
                  >
                    {selectedTabId === tool.id && (
                      <m.div
                        className={cn("absolute inset-0 rounded-inxl border border-default bg-default")}
                        layoutId='sidebar-selected-item'
                      />
                    )}
                    <div className={cn("relative z-10 flex flex-wrap items-center justify-between")}>
                      <div className='flex items-center gap-10 truncate py-4'>
                        {toolTabData?.icon && (
                          <div className='flex-shrink-0'>
                            {React.isValidElement(toolTabData.icon) ? (
                              toolTabData.icon
                            ) : (
                              <Image className='h-20 w-20' resource={toolTabData.icon} />
                            )}
                          </div>
                        )}
                        <span className='f-ui-06 truncate whitespace-nowrap uppercase'>{tool.label}</span>
                      </div>
                    </div>
                  </RadioGroup.Item>
                );
              })}
            </div>
          )}

          {/* Transcriptions Section */}
          {transcriptions && transcriptions.length > 0 && (
            <div className='mb-6'>
              <div className='p-12 opacity-80'>
                <h3 className='f-ui-03 mb-4'>{t("sidebar.transcripts")}</h3>
              </div>
              {transcriptions.map(transcription => {
                const transcriptionTabData = tabData[transcription.id];
                return (
                  <RadioGroup.Item
                    className='relative w-full rounded-lg p-12 focus:outline-darkest'
                    disabled={disabled}
                    key={transcription.id}
                    value={transcription.id}
                  >
                    {selectedTabId === transcription.id && (
                      <m.div
                        className={cn("absolute inset-0 rounded-inxl border border-default bg-default")}
                        layoutId='sidebar-selected-item'
                      />
                    )}
                    <div className={cn("relative z-10 flex flex-wrap items-center justify-between")}>
                      <div className='flex items-center gap-10 truncate py-4'>
                        {transcriptionTabData?.icon && (
                          <div className='flex-shrink-0'>
                            {React.isValidElement(transcriptionTabData.icon) ? (
                              transcriptionTabData.icon
                            ) : (
                              <Image className='h-20 w-20' resource={transcriptionTabData.icon} />
                            )}
                          </div>
                        )}
                        <span className='f-ui-06 whitespace-nowrap uppercase'>{transcription.label}</span>
                      </div>
                    </div>
                  </RadioGroup.Item>
                );
              })}
            </div>
          )}
        </RadioGroup.Root>
      </div>

      {withUploadFile && <OpenFileButton openFileDialog={openFileDialog} variant='elevated' />}
    </div>
  );
};

function TabViewRenderer({ tabData, openFileDialog }: { openFileDialog: () => void; tabData: TabData }) {
  const t = useSTTTranslationCtx();
  const { variant, actions } = useSTTContext();

  switch (tabData.componentId) {
    case "url-input-tool":
      return (
        <>
          {tabData.isTranscribing ? (
            <TranscribingView
              cancel={() => {
                tabData.abortController?.abort();
              }}
              description={t("widget.transcribing.description")}
              title={t("widget.transcribing.title")}
            />
          ) : (
            <STTfromURL {...(tabData.urlInputTexts || {})} />
          )}
        </>
      );
    case "transcription-viewer":
      return (
        <>
          {tabData.uri && variant === "default" && <STTAudioPlayer openFileDialog={openFileDialog} uri={tabData.uri} />}
          <div
            className={cn(
              "flex-grow overflow-hidden rounded-b-inxl rounded-t-md",
              variant === "simple" ? "" : "shadow-xs",
            )}
          >
            {tabData.isTranscribing ? (
              <TranscribingView
                cancel={() => {
                  tabData.abortController?.abort();
                  actions.resetGlobalSTT();
                }}
                description={t("widget.transcribing.description")}
                title={t("widget.transcribing.title")}
              />
            ) : (
              <>
                <TranscriptView segments={tabData.transcription?.segments || []} />
                <ResetFileButton
                  className={clsx("absolute", variant === "simple" ? "right-10 top-10" : "bottom-10 right-10")}
                />
              </>
            )}
          </div>
          {/* Audio player positioned below the transcript view for simple variant */}
          {tabData.uri && variant === "simple" && <STTAudioPlayer openFileDialog={openFileDialog} uri={tabData.uri} />}
        </>
      );
    default:
      return null;
  }
}

function GlobalSTTRenderer({
  getRootProps,
  isDragActive,
  openFileDialog,
}: {
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  isDragActive: boolean;
  openFileDialog: () => void;
}) {
  const { globalSTT, isRealtimeEnabled } = useSTTContext();
  const t = useSTTTranslationCtx();
  const variant = useSTTVariant();

  // Default view - show realtime UI when feature flag is enabled, or when explicitly in realtime mode
  if (!globalSTT.isTranscribing && !globalSTT.transcription?.segments) {
    if (isRealtimeEnabled) {
      return <STTRealtimeUI openFileDialog={openFileDialog} variant={variant} />;
    }

    // Original layout when realtime is disabled
    return (
      <div
        {...getRootProps({
          className: cn(
            "flex flex-1 flex-col items-center justify-center m-14 cursor-pointer relative bg-default shadow-xs",
            isDragActive && "bg-[#F9FAFC]",
            variant === "simple" ? "rounded-[20px]" : "rounded-inxl",
          ),
        })}
      >
        <div className='mb-20 flex items-center justify-center gap-12'>
          {globalSTT.fromIcon}
          <ChevronRight16 className='text-white' />
          {globalSTT.toIcon}
        </div>
        <p className='f-description-04 max-w-[300px] text-center'>{t("widget.help_text_v1_5")}</p>
        <OpenFileButton openFileDialog={openFileDialog} className='mt-12' />
      </div>
    );
  }

  // loading / transcript view
  return (
    <>
      <STTAudioPlayer openFileDialog={openFileDialog} uri={globalSTT.uri || ""} />
      <div
        className={clsx(
          "flex-grow overflow-hidden shadow-xs",
          variant === "simple" ? "rounded-[1.25rem]" : "rounded-b-inxl rounded-t-md",
        )}
      >
        {globalSTT.isTranscribing ? (
          <TranscribingView
            cancel={() => {
              globalSTT.abortController?.abort();
            }}
            description={t("widget.transcribing.description")}
            title={t("widget.transcribing.title")}
          />
        ) : (
          <>
            <TranscriptView segments={globalSTT.transcription?.segments || []} />
            <ResetFileButton className='absolute bottom-10 right-10' />
          </>
        )}
      </div>
    </>
  );
}

const ResetFileButton = ({
  className,
  variant = "secondary",
}: {
  className?: string;
  variant?: "secondary" | "elevated";
}) => {
  const { resetGlobalSTT } = useSTTActions();

  const t = useSTTTranslationCtx();
  return (
    <>
      <Button
        aria-label={t("widget.reset")}
        className={cn("relative inline-flex h-auto items-center justify-center rounded-full p-12", className)}
        onClick={() => {
          resetGlobalSTT();
        }}
        variant={variant}
      >
        <RotateCcw
          className='h-20 w-20 text-lighter'
          style={{
            strokeWidth: "2.5px",
          }}
        />
      </Button>
    </>
  );
};

// Realtime-only STT Component
export type STTRealtimeProps = Pick<BlockDemoPropsV2, "initiallyVisible" | "size" | "variant">;

export const STTRealtimeDemo = memo(STTRealtimeComponent);

const simpleWrapperVariants = cva("", {
  variants: {
    size: {
      sm: "tw-h-52",
      md: "tw-h-64",
      lg: "tw-h-[21.25rem] md:tw-h-[23.5rem]",
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

export function STTRealtimeComponent({ initiallyVisible, size, variant }: STTRealtimeProps) {
  if (variant === "simple") {
    return (
      <OmniboxSimpleWrapper className={simpleWrapperVariants({ size: size as "sm" | "md" | "lg" })}>
        <STTRealtimeUI variant={variant} />
      </OmniboxSimpleWrapper>
    );
  }

  return (
    <OmniboxComponentWrapper
      component='stt'
      initiallyVisible={initiallyVisible}
      size={size || "lg"}
      withBorder={true}
      shadow='combined'
    >
      <div className='relative flex h-full w-full flex-col overflow-hidden'>
        <STTRealtimeUI variant={variant} />
      </div>
    </OmniboxComponentWrapper>
  );
}
